import { supabase } from "@/integrations/supabase/client";
import { useTelegram } from "@/contexts/UnifiedAuthContext";

interface PaymentResponse {
  confirmation_url: string;
}

/**
 * Инициирует процесс оплаты выбранного пакета минут
 * @param packageId ID выбранного пакета минут
 * @param email Электронная почта для отправки чека
 * @returns URL для перехода на страницу оплаты
 */
export const initiatePackagePayment = async (
  packageId: string,
  email: string
): Promise<string> => {
  try {
    // Получаем текущую сессию пользователя
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("errors.unauthorized");
    }

    // Получаем URL для API платежей из переменных окружения
    const paymentUrl = import.meta.env.VITE_PAYMENT_URL;

    if (!paymentUrl) {
      throw new Error("errors.payment.config_missing");
    }

    // Выполняем запрос к платежному API
    const response = await fetch(paymentUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        package_id: packageId,
        email: email,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Ошибка платежного сервиса: ${response.status}`
      );
    }

    const data: PaymentResponse = await response.json();

    return data.confirmation_url;
  } catch (error) {
    console.error("Ошибка при инициализации платежа:", error);
    throw error;
  }
};

export const usePaymentPage = () => {
  const { isTelegram } = useTelegram();

  const openPayment = (url: string): Window | null => {
    // Если приложение запущено в Telegram, используем WebApp API
    if (isTelegram) {
      console.log("Opening payment page via Telegram WebApp API:", url);

      // Используем openLink для внешних ссылок
      if (window.Telegram?.WebApp?.openLink) {
        window.Telegram.WebApp.openLink(url);
        return null; // Telegram API не возвращает window object
      } else {
        // Fallback для старых версий Telegram
        console.warn(
          "Telegram WebApp openLink не доступен, используем fallback"
        );
        return window.open(url, "_blank");
      }
    }

    // Для обычных браузеров используем стандартный подход
    return window.open(url, "_blank");
  };

  return { openPayment };
};
